<template>
  <div class="live-request-card minimalistic-card" :class="getStatusClass(request.status)">
    <div class="minimalistic-content">
      <div class="campaign-info">
        <div class="campaign-id">
          {{ $t("dashboard.live_request_campaign_id", "Campaign ID") }}: {{ request.campaign_id }}
        </div>
        <div class="campaign-name">{{ campaignName }}</div>
        <div class="status-message">
          <svg
            v-if="request.status === 'approved'"
            class="status-icon"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
          </svg>
          <svg
            v-else-if="request.status === 'rejected'"
            class="status-icon"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path
              d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
            />
          </svg>
          {{ statusMessage }}
        </div>
      </div>
      <button
        class="hide-btn"
        @click="handleHide"
        :title="$t('dashboard.live_request_hide', 'Hide this request')"
      >
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path
            d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
          />
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
import { defineComponent } from "vue";

export default defineComponent({
  name: "LiveRequestMinimalisticCard",

  props: {
    request: {
      type: Object,
      required: true,
    },
  },

  computed: {
    campaignName() {
      return this.request.campaign?.campaigntitle || "";
    },

    statusMessage() {
      if (this.request.status === "approved") {
        return this.$t("dashboard.live_request_approved_message", "Campaign successfully approved");
      } else if (this.request.status === "rejected") {
        return this.$t("dashboard.live_request_rejected_message", "Campaign rejected");
      }
      return "";
    },
  },

  methods: {
    handleHide() {
      this.$emit("hide", this.request.id);
    },

    getStatusClass(status) {
      return {
        "status-approved": status === "approved",
        "status-rejected": status === "rejected",
      };
    },
  },
});
</script>

<style lang="scss" scoped>
.live-request-card {
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;

  &.minimalistic-card {
    border: none;
    border-radius: 6px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
    margin-bottom: 12px;

    &.status-approved {
      background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
      border-left: 4px solid #28a745;

      [dir="rtl"] & {
        border-left: none;
        border-right: 4px solid #28a745;
      }

      .status-message {
        color: #155724;
      }
    }

    &.status-rejected {
      background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%);
      border-left: 4px solid #dc3545;

      [dir="rtl"] & {
        border-left: none;
        border-right: 4px solid #dc3545;
      }

      .status-message {
        color: #721c24;
      }
    }

    .minimalistic-content {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px 20px;

      [dir="rtl"] & {
        flex-direction: row-reverse;
      }

      .campaign-info {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 6px;

        [dir="rtl"] & {
          align-items: flex-end;
        }

        .campaign-id {
          font-size: 13px;
          font-weight: 600;
          color: #495057;
        }

        .campaign-name {
          font-size: 16px;
          font-weight: 700;
          color: #212529;
        }

        .status-message {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          font-weight: 600;

          [dir="rtl"] & {
            flex-direction: row-reverse;
          }

          .status-icon {
            width: 18px;
            height: 18px;
          }
        }
      }

      .hide-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        padding: 0;
        background: rgba(255, 255, 255, 0.8);
        border: 1px solid rgba(0, 0, 0, 0.1);
        border-radius: 50%;
        cursor: pointer;
        color: #6c757d;
        transition: all 0.2s ease;
        flex-shrink: 0;

        svg {
          width: 18px;
          height: 18px;
        }

        &:hover {
          background-color: rgba(255, 255, 255, 1);
          border-color: #dc3545;
          color: #dc3545;
          transform: scale(1.1);
        }

        &:focus {
          outline: none;
          box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.25);
        }
      }
    }
  }
}
</style>
